import React, { useRef } from 'react';
import { getApiUrl } from '../../config/apiConfig';

interface JobMatcherCardProps {
  jobDescFile: File | null;
  setJobDescFile: React.Dispatch<React.SetStateAction<File | null>>;
  jobDescription: string;
  setJobDescription: React.Dispatch<React.SetStateAction<string>>;
  jobMatchData: any;
  setJobMatchData: React.Dispatch<React.SetStateAction<any>>;
  isUploading: boolean;
  setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;
  uploadError: string | null;
  setUploadError: React.Dispatch<React.SetStateAction<string | null>>;
  fileError: string;
  setFileError: React.Dispatch<React.SetStateAction<string>>;
  copySuccess: {[key: string]: string};
  setCopySuccess: React.Dispatch<React.SetStateAction<{[key: string]: string}>>;
  processingDone: {[key: string]: boolean};
  setProcessingDone: React.Dispatch<React.SetStateAction<{[key: string]: boolean}>>;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>, setFile: React.Dispatch<React.SetStateAction<File | null>>) => void;
  copyToClipboard: (text: string, type: string) => void;
  clearForm: (fileType: string) => void;
}

const JobMatcherCard: React.FC<JobMatcherCardProps> = ({
  jobDescFile,
  setJobDescFile,
  jobDescription,
  setJobDescription,
  jobMatchData,
  setJobMatchData,
  isUploading,
  setIsUploading,
  uploadError,
  setUploadError,
  fileError,
  copySuccess,
  processingDone,
  handleFileChange,
  copyToClipboard,
  clearForm
}) => {
  const jobDescFileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async () => {
    if (!jobDescFile) return;
    
    setIsUploading(true);
    setUploadError(null);
    
    try {
      // Use the API config to get the correct endpoint
      const apiUrl = getApiUrl('JOB_MATCHER');
      
      const formData = new FormData();
      formData.append('file', jobDescFile);
      
      // Include job description for job matcher requests
      if (jobDescription) {
        formData.append('jobDescription', jobDescription);
      } else {
        // Add a default job description if none is provided for job matcher
        formData.append('jobDescription', 'Software Developer with experience in web development');
      }
      
      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          body: formData,
        });
        
        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        setJobMatchData(data);
      } catch (fetchError) {
        // Check if it's a network error (connection refused)
        if (fetchError instanceof TypeError && fetchError.message.includes('Failed to fetch')) {
          throw new Error('Backend server is not running. Please start the backend server on port 5275.');
        } else {
          throw fetchError;
        }
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <h2 className="text-xl font-bold mb-4 dark:text-white">Job Matcher</h2>
      <p className="text-gray-600 dark:text-gray-300 mb-4">
        Compare a resume against a job description to find the match percentage.
      </p>
      
      <div className="mb-6 border p-4 rounded-md bg-white dark:bg-gray-800 dark:border-gray-700">
        <h3 className="font-medium mb-2 dark:text-white">Job Description</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Enter or paste the job description you want to match against</p>
        <textarea 
          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white" 
          rows={5} 
          placeholder="Paste the job description here..."
          value={jobDescription}
          onChange={(e) => setJobDescription(e.target.value)}
        ></textarea>
      </div>
      
      <div className="mb-6 border dark:border-gray-700 p-4 rounded-md bg-white dark:bg-gray-800">
        <h3 className="font-medium mb-2 dark:text-white">Upload Resume</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Upload the resume you want to match against the job description</p>
        <div className="flex flex-col space-y-4">
          <div className="flex items-center">
            <label htmlFor="job-desc-file" className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 py-2 px-4 rounded cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-800/40 transition-colors">
              Choose File
            </label>
            <span className="ml-3 text-gray-500 dark:text-gray-400 text-sm">{jobDescFile ? jobDescFile.name : 'No file chosen'}</span>
            <input id="job-desc-file" type="file" ref={jobDescFileInputRef} onChange={(e) => handleFileChange(e, setJobDescFile)} accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" />
          </div>
          <div className="flex items-center space-x-2">
            <button onClick={handleFileUpload} disabled={!jobDescFile || isUploading} className="bg-blue-600 dark:bg-blue-700 text-white py-2 px-4 rounded hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:bg-blue-300 dark:disabled:bg-blue-800 disabled:cursor-not-allowed flex items-center">
              {isUploading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                'Process Resume'
              )}
            </button>
            <button onClick={() => clearForm('job')} disabled={!jobDescFile && !jobMatchData && !jobDescription} className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-2 px-4 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-600 disabled:cursor-not-allowed">
              Clear
            </button>
          </div>
        </div>
        {fileError && <p className="text-red-500 text-sm mt-2">{fileError}</p>}
        {uploadError && <p className="text-red-500 text-sm mt-2">{uploadError}</p>}
      </div>
      
      {/* Job Match Results */}
      {jobMatchData && (
        <div className="mb-6 border dark:border-gray-700 p-4 rounded-md bg-white dark:bg-gray-800">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-medium dark:text-white">Job Match Results</h3>
            <button 
              onClick={() => {
                // Format the data in AsciiDoc format
                const confidenceScore = jobMatchData.matchScore || 0;
                
                // Ensure all data is in array format
                const strengths = Array.isArray(jobMatchData.strengths) 
                  ? jobMatchData.strengths 
                  : typeof jobMatchData.strengths === 'string' 
                    ? [jobMatchData.strengths] 
                    : [];
                    
                const weaknesses = Array.isArray(jobMatchData.weaknesses) 
                  ? jobMatchData.weaknesses 
                  : typeof jobMatchData.weaknesses === 'string' 
                    ? [jobMatchData.weaknesses] 
                    : [];
                
                const asciiDocContent = `= Job Match Results\n\n== Confidence Score (1-10)\n${confidenceScore}/10\n\n== Strengths\n${strengths.map((skill: string) => `* ${skill}`).join('\n')}\n\n== Weaknesses\n${weaknesses.map((skill: string) => `* ${skill}`).join('\n')}`;
                
                copyToClipboard(asciiDocContent, 'job');
              }}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              {copySuccess.job || 'Copy to Clipboard'}
            </button>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded overflow-auto">
            {/* Confidence Score (1-10) */}
            <div className="mb-4">
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Confidence Score (1-10)</h4>
              <div className="flex items-center">
                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-4 mr-4">
                  <div className="bg-blue-600 h-4 rounded-full" style={{ width: `${(jobMatchData.matchScore || 0) * 10}%` }}></div>
                </div>
                <span className="text-lg font-bold dark:text-white">{jobMatchData.matchScore || 0}/10</span>
              </div>
            </div>
            
            {/* Strengths (Matched Skills) */}
            <div className="mb-4">
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Strengths</h4>
              {(() => {
                // Ensure strengths is an array
                const strengths = Array.isArray(jobMatchData.strengths) 
                  ? jobMatchData.strengths 
                  : typeof jobMatchData.strengths === 'string' 
                    ? [jobMatchData.strengths] 
                    : [];
                    
                return strengths.length > 0 ? (
                  <ul className="list-disc pl-5 space-y-1">
                    {strengths.map((skill: string, index: number) => (
                      <li key={index} className="text-green-700 dark:text-green-400">{skill}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 italic">No strengths found</p>
                );
              })()}
            </div>
            
            {/* Weaknesses (Missing Skills) */}
            <div>
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Weaknesses</h4>
              {(() => {
                // Ensure weaknesses is an array
                const weaknesses = Array.isArray(jobMatchData.weaknesses) 
                  ? jobMatchData.weaknesses 
                  : typeof jobMatchData.weaknesses === 'string' 
                    ? [jobMatchData.weaknesses] 
                    : [];
                    
                return weaknesses.length > 0 ? (
                  <ul className="list-disc pl-5 space-y-1">
                    {weaknesses.map((skill: string, index: number) => (
                      <li key={index} className="text-red-700 dark:text-red-400">{skill}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 italic">No weaknesses found</p>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      
      {!jobMatchData && (
        <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg mb-4">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Upload a resume and provide a job description to see the match percentage</p>
          <p className="dark:text-gray-300">This tool will help you:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1 dark:text-gray-300">
            <li>Compare resume skills to job requirements</li>
            <li>Identify skill gaps</li>
            <li>Generate a match percentage score</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default JobMatcherCard;
